import * as angular from 'angular';

import { ComponentDefinition, TagDefinition, TagDateValue } from '../../models';
import { RangeChartPaneConfiguration } from '../../wtFramework';
import { IDateRangeService, IRequestService } from '../../services';
import { refreshClick } from '../../common';

class TagStatisticsChartController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    selectedTag: TagDefinition;
    panes: RangeChartPaneConfiguration<TagDateValue, string>[];
    ranges: number[][];
    data: TagDateValue[];
    xDomain: [string, string];
    formController?: angular.IFormController;

    private _listener: Function;

    static $inject = ['$scope', 'dateRangeService', 'requestService'];

    constructor(
        private _$scope: angular.IScope,
        private _dateRangeService: IDateRangeService,
        private _requestService: IRequestService
    ) {
    }

    $onInit() {
        this._$scope.$on(refreshClick, (event, data) => {
            if (this.selectedTag) {
                this.refreshData(!!data.force);
            }
        });
        this.init();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['selectedTag'] && changesObj['selectedTag'].currentValue) {
            this.init();
        }
    }

    private init() {
        if (this.selectedTag) {
            this.refresh();

            // free previous listener if any
            if (this._listener) {
                this._listener();
            }

            // listen for tag changes to report on chart
            this._listener = this._$scope.$watch(() => this.selectedTag, () => {
                const tag = this.selectedTag;
                if (tag) {
                    this.ranges = [[tag.min, tag.max], [0, tag.stdDevThreshold]];
                    this.refresh();
                }
            }, true);
        }

    }

    private refreshPanes() {
        const tag = this.selectedTag;
        this.panes = [
            {
                height: 1,
                seriesFormat: {
                    selector: d => `${d.guid}`,
                    identifier: `${tag?.guid}`,
                    value: d => d.value,
                    name: 'Average'
                },
                brushCallback: extent => {
                    this.selectedTag.min = extent[0];
                    this.selectedTag.max = extent[1];
                    if (this.formController) {
                        this.formController['minProperty'].$setDirty();
                        this.formController['maxProperty'].$setDirty();
                    }
                    this._$scope.$apply();
                },
                lines: [this.selectedTag]
                    .filter(t => t?.defaultValue !== null && t?.defaultValue !== undefined)
                    .map(t => {
                        return {
                            value: t.defaultValue,
                            color: '#c36'
                        };
                    })
            },
            {
                height: 1,
                seriesFormat: {
                    selector: d => d.guid,
                    identifier: `${tag?.guid}`,
                    value: d => (d.secondaryValue as any) === 'NaN' ? null : d.secondaryValue,
                    name: 'Std. dev.'
                },
                isLockedBrush: true,
                brushCallback: extent => {
                    if (this.selectedTag) {
                        this.selectedTag.stdDevThreshold = extent[1];
                        if (this.formController) {
                            this.formController['stdDevThresholdProperty'].$setDirty();
                        }
                        this._$scope.$apply();
                    }
                }
            }
        ];
        this.ranges = [[tag?.min, tag?.max], [0, tag?.stdDevThreshold]];
    }

    private refresh() {
        if (this.selectedTag) {
            this.refreshPanes();
            this.refreshData();
        }
    }

    private refreshData(force: boolean = false) {
        this.xDomain = [this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string];

        if (this.selectedTag) {
            this.isLoading = true;
            const endpoint = 'values/averageAndStandardDeviation';
            const configuration = {
                beginDate: this._dateRangeService.beginDate() as string,
                endDate: this._dateRangeService.endDate() as string,
                guid: this.selectedTag.guid
            };
            if (force) {
                this._requestService.cleanCachedPostValue(endpoint, configuration);
            }
            this._requestService.cachedPostAtV1(endpoint, configuration)
                .then(data => {
                    this.data = [...data as TagDateValue[]];
                })
                .finally(() => this.isLoading = false);
        }
    }
}

export const wtTagStatisticsChart: ComponentDefinition = {
    componentId: 'wtTagStatisticsChart',
    controller: TagStatisticsChartController,
    template: require('./tagStatisticsChart.component.html'),
    controllerAs: 'vm',
    bindings: {
        data: '=',
        formController: '<?',
        isEditMode: '<',
        selectedTag: '<',
    }
};